import { useCallback } from 'react'
import { useQueryClient } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { usePolling } from '../../../../../hooks/usePolling'
import { useImportedInvoicePaymentFlags } from '../../../hooks/useImportedInvoicePaymentFlags'
import { ImportedInvoice } from '../../../types/importedInvoice'
import { ImportedInvoicePaymentFlags } from '../../../types/importedInvoicePaymentFlags'
import { getImportedInvoiceQueryKey } from '../../../utils/getImportedInvoiceQueryKey'

// Billy's balance event usually lands within 2–6 s. After about 30 s the drawer stops asking and
// keeps showing what Billy says.
const PAYMENT_POLLING_INTERVAL = 1500
const PAYMENT_POLLING_MAX_ATTEMPTS = 20

interface UseImportedInvoiceDrawerPaymentParams {
  importedInvoiceId?: string
  invoice?: ImportedInvoice
}

/** Refetches the imported invoice while its payment status lags behind the Billy balance. */
export const useImportedInvoiceDrawerPayment = ({
  importedInvoiceId,
  invoice,
}: UseImportedInvoiceDrawerPaymentParams): ImportedInvoicePaymentFlags => {
  const queryClient = useQueryClient()
  const { organization } = useUserOrganization()
  const organizationId = organization?.id

  // `paymentStatus` only comes with `approved`, and stays unset until the row has been posted.
  const paymentFlags = useImportedInvoicePaymentFlags({
    externalInvoiceId: invoice?.externalInvoiceId,
    paymentStatus: invoice?.approved ? invoice.paymentStatus : undefined,
  })

  const handlePoll = useCallback(() => {
    queryClient.invalidateQueries(getImportedInvoiceQueryKey(organizationId, importedInvoiceId))
  }, [importedInvoiceId, organizationId, queryClient])

  usePolling({
    interval: PAYMENT_POLLING_INTERVAL,
    isEnabled: paymentFlags.isPending,
    maxAttempts: PAYMENT_POLLING_MAX_ATTEMPTS,
    onPoll: handlePoll,
  })

  return paymentFlags
}
